import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { formatDate } from '@/lib';
import { type AccountActivityItem } from '@/portal-api.definition';
import { ChevronLeft, ChevronRight, Close, Filter, Info, Success } from '@nosinovacao/fe-icons';
import { cn, FloatingActionButton, If, useI18n } from '@nosinovacao/nosid-mfe-common';
import { Link } from '@tanstack/react-router';
import { type ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import { type ActivityLogsProps } from '../-model/props';
import { getDevice, normalizeDeviceName } from '../-model/utils';

const SCROLL_AMOUNT = 150;

export function ActivityLogsLight({
  loading,
  logs,
  applicationNames,
  loadMore,
  currentQuery,
  filterItems,
}: Readonly<ActivityLogsProps>) {
  const { t } = useI18n();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, filterItems]);

  const scroll = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="relative isolate -mx-8 grid gap-2 overflow-y-auto px-8 max-tablet:pb-8"
      onScroll={(event) => {
        if (loading) return;
        const el = event.target as HTMLDivElement;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        // If scrolled to last 20%
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
          loadMore?.();
        }
      }}
    >
      <If condition={!!filterItems.length}>
        <div
          ref={scrollRef}
          className="no-scrollbar sticky top-0 z-10 -mx-8 flex h-max items-center gap-4 overflow-auto bg-nos-neutral-0 px-8 pb-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
          onScroll={updateScrollState}
        >
          {canScrollLeft && (
            <button
              type="button"
              aria-label="Scroll left"
              data-tid="button-filter-scroll-left"
              className="sticky -left-8 z-10 flex h-8 w-14 shrink-0 items-center justify-center bg-gradient-to-r from-nos-neutral-0 to-transparent"
              onClick={() => scroll('left')}
            >
              <ChevronLeft width={32} height={32} className="text-nos-neutral-900" />
            </button>
          )}
          {filterItems.map((f) => (
            <Link
              to="/security/account-activity"
              search={f.query}
              key={f.id}
              data-tid={`link-filter-${f.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-nos-neutral-100 px-4 py-1 -outline-offset-2"
              replace
            >
              <span className="text-nowrap text-nos-neutral-900 font-ui-8">{t(f.label)}</span>
              <div className="size-4 shrink-0">
                <Close width={16} height={16} />
              </div>
            </Link>
          ))}
          {canScrollRight && (
            <button
              type="button"
              aria-label="Scroll right"
              data-tid="button-filter-scroll-right"
              className="sticky -right-8 z-10 flex h-8 w-14 shrink-0 items-center justify-center bg-gradient-to-l from-nos-neutral-0 to-transparent"
              onClick={() => scroll('right')}
            >
              <ChevronRight width={32} height={32} className="text-nos-neutral-900" />
            </button>
          )}
        </div>
      </If>
      <If condition={!logs.length && !loading}>
        <span
          data-tid="text-no-logs"
          className="text-center font-medium text-nos-neutral-900 font-text-p1"
        >
          {t('AccountActivity.List.NoLogs')}
        </span>
      </If>
      {logs.map((log) => (
        <LogItem
          key={`${log.ActivityDate}-${log.ActivityType}`}
          {...log}
          applicationNames={applicationNames}
        />
      ))}
      <If condition={loading}>
        <SkeletonLight loading count={10} className="h-[210px] rounded" />
      </If>
      <FloatingActionButton
        data-tid="button-filter"
        skin="light"
        className="fixed bottom-4 right-4"
        asChild
      >
        <Link to="/security/account-activity/filters" search={currentQuery}>
          <Filter />
        </Link>
      </FloatingActionButton>
    </div>
  );
}

function getActivityStatus(type: string): {
  status: 'success' | 'error' | 'info';
  icon: ComponentType<{ className?: string; width?: number; height?: number }>;
} {
  switch (type) {
    case 'UserLocked':
    case 'IncorrectPasswordEntered':
      return {
        icon: Info,
        status: 'error',
      };
    case 'SuccessfulAuthentication':
    case 'SuccessfulExternalAuthentication':
    case 'UsernameChanged':
    case 'UsernameRemoved':
    case 'PasswordChanged':
    case 'ProfileUpdated':
    case 'CustomerAccountBound':
    case 'CustomerAccountUnbound':
    case 'BaseUserAdded':
    case 'BaseUserRemoved':
    case 'UserCreated':
    case 'UserRemoved':
    case 'UsernameAliasDeleted':
    case 'TwoFactorAuthenticationDisabled':
    case 'TwoFactorAuthenticationEnabled':
    case 'MultiFactorAuthenticationEnabled':
    case 'MultiFactorAuthenticationDisabled':
      return {
        icon: Success,
        status: 'success',
      };
    default:
      return {
        icon: Info,
        status: 'info',
      };
  }
}

function LogItem(log: AccountActivityItem & { applicationNames?: Record<string, string> }) {
  const { t } = useI18n();
  const device = getDevice(log);
  const normalizedDevice = device ? normalizeDeviceName(device) : null;
  const app = log.applicationNames?.[log.ClientAppId ?? ''];
  const { icon: StatusIcon, status } = getActivityStatus(log.ActivityType);
  return (
    <div className="grid gap-6 rounded border border-nos-neutral-200 bg-nos-neutral-0 p-4">
      <div className="grid grid-cols-[1fr,auto] gap-1">
        <span
          data-tid="text-activity-type"
          className="font-medium text-nos-neutral-900 font-text-p1"
        >
          {t(`AccountActivity.Type.${log.ActivityType}`)}
        </span>
        <div className="size-6">
          <StatusIcon
            className={cn(
              status === 'error' && 'text-nos-red',
              status === 'success' && 'text-nos-green',
              status === 'info' && 'text-nos-blue',
            )}
            width={24}
            height={24}
          />
        </div>
        <span data-tid="text-activity-date" className="text-nos-neutral-500 font-text-p2">
          {formatDate(log.ActivityDate)}
        </span>
      </div>
      <div className="grid gap-4">
        <div className="grid gap-1">
          <span data-tid="text-equipment-label" className="text-nos-neutral-400 font-text-p2">
            {t('AccountActivity.List.Equipment')}
          </span>
          <span data-tid="text-equipment-value" className="text-nos-neutral-900 font-text-p1">
            {normalizedDevice || '-'}
          </span>
        </div>
        <div className="grid gap-1">
          <span data-tid="text-app-label" className="text-nos-neutral-400 font-text-p2">
            {t('AccountActivity.List.App')}
          </span>
          <span data-tid="text-app-value" className="text-nos-neutral-900 font-text-p1">
            {app || '-'}
          </span>
        </div>
      </div>
    </div>
  );
}
